import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedText from '../components/AnimatedText';
import Footer from '../components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { FaQrcode, FaUserFriends, FaChartBar, FaKey, FaRegLifeRing, FaMobileAlt, FaUserShield, FaLink } from 'react-icons/fa';
import { ArrowRight, ChevronDown } from 'react-feather';

// ---- DATA ----
const bentoFeatures = [
  { icon: <FaUserFriends />, title: 'Grupos', description: 'Crea un grupo, uno paga, todos entran.', className: 'md:col-span-2 md:row-span-2' },
  { icon: <FaQrcode />, title: 'Entradas QR', description: 'Acceso instantáneo. Sin papeles.', className: 'md:col-span-1' },
  { icon: <FaChartBar />, title: 'Analytics', description: 'Data real para decisiones reales.', className: 'md:col-span-1' },
  { icon: <FaUserShield />, title: 'Admin Control', description: 'Gestión de aforo y listas en vivo.', className: 'md:col-span-2' },
  { icon: <FaLink />, title: 'Promotor Links', description: 'Tracking exacto de ventas.', className: 'md:col-span-1' },
  { icon: <FaMobileAlt />, title: 'Mobile First', description: 'Diseñado para la pista de baile.', className: 'md:col-span-1' },
  { icon: <FaKey />, title: 'Seguridad', description: 'Encriptación de grado bancario.', className: 'md:col-span-1' },
  { icon: <FaRegLifeRing />, title: 'Soporte 24/7', description: 'Siempre activos.', className: 'md:col-span-1' },
];

const content = {
  guests: {
      title: "Para la Fiesta",
      subtitle: "Tu noche, simplificada.",
      images: [
        { src: "/phone_mockups/Guests01.png", label: "Descubre Eventos" }, // CAMBIAR POR TU SCREENSHOT
        { src: "/phone_mockups/Guests02.png", label: "Tu Ticket QR" },     // CAMBIAR POR TU SCREENSHOT
        { src: "/phone_mockups/Guests03.png", label: "Grupos Sociales" }   // CAMBIAR POR TU SCREENSHOT
      ]
    }, 
    hosts: {
      title: "Para Organizadores",
      subtitle: "Control total, en tiempo real.",
      images: [
        { src: "/phone_mockups/Hosts01.png", label: "Dashboard Métricas" }, // CAMBIAR POR TU SCREENSHOT
        { src: "/phone_mockups/Hosts02.png", label: "Gestión de Equipo" },  // CAMBIAR POR TU SCREENSHOT
        { src: "/phone_mockups/Hosts03.png", label: "Scanner Puerta" }      // CAMBIAR POR TU SCREENSHOT
      ]
    }
};

const users = [
  "/devs/antonella.png",
  "/devs/martin.png",
  "/devs/mauricio.jpg",
  "/devs/pollo.png",
  "/devs/alele.png",
  "/devs/sebastian.png",
  "/devs/diego.png",
];

// ---- COMPONENTS ----

const Marquee = () => (
  <div className="relative flex overflow-hidden py-4 bg-white/5 border-y border-white/5 backdrop-blur-sm">
    <div className="animate-marquee whitespace-nowrap flex gap-12 text-white/40 font-bold uppercase tracking-[0.2em] text-xs md:text-sm">
      <span>La Juerga a Otro Nivel</span> • <span>Tu Gente Primero</span> • <span>Ingresa Al Toque</span> • <span>Sin Colas Ni Listas</span> • 
      <span>La Juerga a Otro Nivel</span> • <span>Tu Gente Primero</span> • <span>Ingresa Al Toque</span> • <span>Sin Colas Ni Listas</span> •
      <span>La Juerga a Otro Nivel</span> • <span>Tu Gente Primero</span> • <span>Ingresa Al Toque</span> • <span>Sin Colas Ni Listas</span> • 
      <span>La Juerga a Otro Nivel</span> • <span>Tu Gente Primero</span> • <span>Ingresa Al Toque</span> • <span>Sin Colas Ni Listas</span> •
    </div>
  </div>
);

function BentoCard({ icon, title, description, className, delay }) {
  return (
    <motion.div
      className={`
        group relative p-6 md:p-8 rounded-3xl border border-white/10 bg-[#0a0a0a] 
        hover:border-white/30 transition-all duration-500 flex flex-col justify-between overflow-hidden
        ${className}
      `}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      {/* Ambient Glow dentro de la tarjeta */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 blur-[50px] rounded-full -mr-16 -mt-16 transition-opacity group-hover:opacity-100" />
      
      <div className="relative z-10">
        <div className="mb-6 text-3xl text-white/60 group-hover:text-white group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <h3 className="text-xl font-medium mb-2 text-white tracking-wide">{title}</h3>
      </div>
      <p className="relative z-10 text-white/40 text-sm leading-relaxed font-light">{description}</p>
    </motion.div>
  );
}

const PhoneMockup = ({ src, label, delay }) => (
  <motion.div
    className="flex flex-col items-center group relative z-10"
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -30 }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="relative w-[240px] md:w-[280px] aspect-[9/19] rounded-[2.5rem] border-[8px] border-[#1a1a1a] bg-black overflow-hidden shadow-2xl shadow-black/80 ring-1 ring-white/10 transition-transform duration-500 group-hover:-translate-y-4">
      {/* Screen */}
      <img src={src} alt={label} className="w-full h-full object-cover" />
      
      {/* Reflection */}
      <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none z-20" />
    </div>
    <p className="mt-8 text-white/40 text-sm font-bold tracking-widest uppercase group-hover:text-white transition-colors">
      {label}
    </p>
  </motion.div>
);


export default function Home() {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 450 : false);
  const navigate = useNavigate();
  const videoRef = useRef(null);

  // 2. Efecto "Nuclear" para forzar el Autoplay en Móviles
  useEffect(() => {
      const video = videoRef.current;
      if (video) {
          video.muted = true;
          video.play().catch(e => console.log("Autoplay bloqueado por el navegador:", e));
      }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 450);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [activeTab, setActiveTab] = useState('guests');

  const sectionVariant = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    // CAMBIO: Fondo negro puro o gris muy oscuro (#050505) para profundidad
    <div className="min-h-screen bg-[#050505] text-white flex flex-col font-sans selection:bg-white selection:text-black">

      {/* --- HERO SECTION (Video Background) --- */}
      <motion.div
        className="relative h-screen w-full overflow-hidden bg-black"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        {/* 1. BACKGROUND: Solución "Nuclear" con HTML Puro */}
        <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-black/40 z-[1]" /> 
            <div 
                className="w-full h-full"
                dangerouslySetInnerHTML={{
                    __html: `
                    <video autoplay muted playsinline loop preload="none" poster="/home.gif" class="w-full h-full object-cover opacity-60">
                        <source src="/home.mp4" type="video/mp4" />
                    </video>
                    `
                }}
            />

            {/* Gradientes (Encima del video) */}
            <div className="absolute inset-0 z-[2] bg-gradient-to-r from-black via-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-32 z-[2] bg-gradient-to-t from-[#050505] to-transparent" />
        </div>

        {/* 2. CONTENIDO: Alineado a la izquierda (Igual que antes) */}
        <div className="relative z-10 w-full h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            
            <div className="max-w-3xl"> 
                
                {/* Etiqueta superior */}
                <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="flex items-center gap-3 mb-8"
                >
                    <div className="h-px w-8 bg-white/60"></div>
                    <span className="text-white/60 text-xs font-bold tracking-[0.2em] uppercase">
                        The Nightlife Standard
                    </span>
                </motion.div>

                {/* ANIMATED TEXT */}
                <div className="mb-8 text-left origin-left transform -ml-1 scale-140 md:scale-110">
                    <AnimatedText />
                </div>

                {/* Subtítulo */}
                <motion.p 
                    className="text-white/50 text-base md:text-lg font-light leading-relaxed mb-10 max-w-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                >
                    La plataforma definitiva para gestionar tus eventos, listas y accesos. 
                    Desde tu cel, con tus amigos, sin complicaciones.
                </motion.p>

                {/* Botón */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="flex flex-col sm:flex-row gap-4"
                >
                    <button
                        onClick={() => navigate('/explore')}
                        className="group max-w-[60vw] sm:max-w-none flex items-center gap-3 px-8 py-4 bg-white text-black rounded-full text-sm font-bold tracking-wide transition-all duration-300 hover:bg-gray-200 shadow-[0_0_20px_-5px_rgba(255,255,255,0.3)]"
                    >
                        EXPLORAR EVENTOS
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0"/>
                    </button>
                    
                </motion.div>

                {/* Scroll Indicator */}
                <motion.div 
                    className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/30 z-20 flex flex-col items-center gap-2"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                >
                    <span className="text-[10px] uppercase tracking-widest">Scroll</span>
                    <ChevronDown size={20} />
                </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* --- MARQUEE --- */}
      <Marquee />

      {/* --- SOCIAL PROOF & STATS --- */}
      <section className="w-full pt-16 pb-0 sm:pt-60 sm:pb-40 bg-[#030303] relative overflow-hidden">
        {/* Ambient Light */}
        <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-white/5 blur-[120px] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                <div>
                    <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
                        +1,500 <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-white/40 to-white/10">Entradas.</span>
                    </h2>
                    <p className="text-white/50 text-lg font-light max-w-md">
                        En solo un mes, miles de personas han dejado atrás las listas de papel. 
                        Únete a la revolución digital.
                    </p>
                </div>
                
                {/* Visual Representation */}
                <div className="relative h-[200px] w-full bg-white/[0.03] border border-white/5 rounded-3xl overflow-hidden flex items-center justify-center">
                    {/* CSS Grid Pattern - SOLUCIÓN: Usamos CSS en vez de imagen externa */}
                    <div 
                        className="absolute inset-0 opacity-20"
                        style={{
                            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                            backgroundSize: '24px 24px'
                        }}
                    />
                    
                    <div className="flex -space-x-6 relative z-10">
                        {users.map((src, index) => (
                          <div className="sm:w-20 w-16 sm:h-20 h-16 rounded-full border-4 border-[#0a0a0a] bg-zinc-800 relative z-10 shadow-xl overflow-hidden">
                            <img 
                                key={index}
                                src={src}
                                alt="User"
                                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300"
                            />
                            </div>
                        ))}
                        <div className="sm:w-20 sm:h-20 w-16 h-16 rounded-full border-4 border-[#0a0a0a] bg-white text-black flex items-center justify-center font-bold text-lg relative z-20">
                            +1k
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* --- SECTION 3: MOCKUPS (Clean) --- */}
      <section className="w-full bg-[#050505] py-32 px-4 relative overflow-hidden">
        
        {/* Glow de fondo dinámico (Cambia sutilmente de posición según el tab) */}
        <motion.div 
            animate={{ 
                left: activeTab === 'guests' ? '30%' : '70%',
                background: activeTab === 'guests' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.05)'
            }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] blur-[120px] rounded-full pointer-events-none" 
        />

        <div className="max-w-7xl mx-auto flex flex-col items-center relative z-10">
          
          {/* HEADER: Título + Toggle Switch */}
          <div className="flex flex-col items-center mb-20 text-center">
            <h2 className="text-4xl md:text-5xl font-light text-white mb-8">
              Dos mundos. <span className="font-serif italic text-white/60">Una App.</span>
            </h2>

            {/* TOGGLE SWITCH PERSONALIZADO */}
            <div className="flex p-1 bg-white/5 rounded-full backdrop-blur-sm border border-white/5 relative">
                {/* Fondo animado del botón activo */}
                <motion.div 
                    className="absolute top-1 bottom-1 bg-white rounded-full shadow-lg z-0"
                    initial={false}
                    animate={{ 
                        left: activeTab === 'guests' ? '4px' : '50%', 
                        width: 'calc(50% - 4px)',
                        x: activeTab === 'hosts' ? '0%' : '0%' // Ajuste fino si es necesario
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />

                <button 
                    onClick={() => setActiveTab('guests')}
                    className={`relative z-10 px-8 py-3 rounded-full text-sm font-bold tracking-wide transition-colors duration-300 ${activeTab === 'guests' ? 'text-black' : 'text-white/60 hover:text-white'}`}
                >
                    GUESTS
                </button>
                <button 
                    onClick={() => setActiveTab('hosts')}
                    className={`relative z-10 px-8 py-3 rounded-full text-sm font-bold tracking-wide transition-colors duration-300 ${activeTab === 'hosts' ? 'text-black' : 'text-white/60 hover:text-white'}`}
                >
                    HOSTS
                </button>
            </div>
            
            {/* Subtítulo dinámico */}
            <AnimatePresence mode='wait'>
                <motion.p
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-8 text-xl text-white/80 font-serif italic"
                >
                    {content[activeTab].subtitle}
                </motion.p>
            </AnimatePresence>
          </div>

          {/* GRID DE TELÉFONOS (3 IMÁGENES) */}
          <div className="w-full min-h-[600px] flex justify-center">
            <AnimatePresence mode="wait">
                <motion.div 
                    key={activeTab} // Clave para reiniciar animación al cambiar tab
                    className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 items-center"
                >
                    {content[activeTab].images.map((img, index) => (
                        <div key={index} className={`${index === 1 ? 'md:-mt-12 z-10' : 'z-0'}`}> 
                            {/* El del medio (index 1) sube un poco para destacar */}
                            <PhoneMockup 
                                src={img.src} 
                                label={img.label} 
                                delay={0.1 * index}
                            />
                        </div>
                    ))}
                </motion.div>
            </AnimatePresence>
          </div>

        </div>
      </section>

      {/* --- HOW IT WORKS --- */}
      <section className="py-24 bg-[#030303] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-20">
                <h2 className="text-3xl md:text-5xl font-light text-white">
                    Simple. <span className="block text-white/30 font-serif italic">Como debe ser.</span>
                </h2>
                <p className="text-white/40 text-sm max-w-xs mt-6 md:mt-0">
                    Eliminamos la fricción entre tú y la fiesta. Tres pasos para asegurar tu noche.
                </p>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
    
            {/* 1. LÍNEA CONECTORA (Desktop: Horizontal) */}
            <div className="hidden md:block absolute top-8 left-10 right-10 h-px bg-gradient-to-r from-white/5 via-white/20 to-white/5 z-0" />
            
            {/* 2. LÍNEA CONECTORA (Mobile: Vertical) - NUEVO */}
            {/* Se posiciona a la izquierda (left-8 correspondientes a la mitad del círculo de 4rem/w-16) */}
            <div className="md:hidden absolute top-0 bottom-0 left-8 w-px bg-gradient-to-b from-white/5 via-white/20 to-transparent z-0" />
            
            {[
                { step: "01", title: "Descubre", desc: "Encuentra el evento o crea uno privado en segundos." },
                { step: "02", title: "Únete", desc: "Regístrate en un clic o añade a tu grupo de amigos." },
                { step: "03", title: "Ingresa", desc: "Un solo QR para todo el grupo. Escaneo rápido." }
            ].map((item, i) => (
                <div key={i} className="relative z-10 flex md:block items-start gap-6 md:gap-0 group">
                    
                    {/* CÍRCULO (STEP) */}
                    {/* En móvil: flex-shrink-0 para que no se aplaste */}
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#030303] border border-white/10 flex items-center justify-center md:mx-auto md:mb-6 text-white/80 font-mono text-lg shadow-[0_0_20px_-5px_rgba(255,255,255,0.1)] group-hover:border-white/30 transition-colors">
                        {item.step}
                    </div>
                    
                    {/* TEXTOS */}
                    {/* En móvil: pt-2 para alinear con el círculo. En desktop: centrado */}
                    <div className="pt-2 md:pt-0 md:text-center">
                        <h3 className="text-xl font-medium text-white mb-2">{item.title}</h3>
                        <p className="text-white/40 text-sm leading-relaxed max-w-[250px] md:mx-auto">{item.desc}</p>
                    </div>
                </div>
            ))}
        </div>
        </div>
      </section>

      {/* --- SECTION 4: EDITORIAL QUOTE (Costa Verde) --- */}
      <motion.section
        className="relative w-full h-[90vh] flex items-center justify-end bg-[#050505] overflow-hidden"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={sectionVariant}
      >
        <img src="/backgrounds/bizarro.jpg" alt="Costa Verde" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        {/* Degradado lateral para legibilidad del texto */}
        <div className="absolute inset-0 bg-gradient-to-l from-[#050505] via-[#050505]/80 to-transparent z-10" />
        
        <div className="relative z-20 w-full max-w-7xl mx-auto px-6 md:px-12 flex justify-end">
          <div className="max-w-xl text-right">
            <motion.h2
              className="text-4xl md:text-6xl font-serif italic text-white mb-10 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              La infraestructura que <br/> tu evento merece.
            </motion.h2>
            
            <div className="space-y-6 text-white/70 text-lg font-light leading-relaxed">
                <p>Sabemos lo que implica construir una comunidad desde cero.</p>
                <p>Desde una experiencia social única hasta un proceso de ingreso sin fricciones, AFTR es el motor invisible que impulsa la noche.</p>
            </div>

            <motion.div
              className="text-white font-serif italic text-2xl mt-12 opacity-80"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              — AFTR
            </motion.div>
          </div>
        </div>
      </motion.section>


      {/* --- SECTION 5: BENTO GRID (Minimalist) --- */}
      {!isMobile && (
        <motion.section
          className="w-full py-32 bg-[#050505] relative border-t border-white/5"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={sectionVariant}
        >
          <div className="max-w-6xl mx-auto px-6"> {/* max-w-6xl para que el grid se vea más compacto y premium */}
            
            <div className="flex flex-col md:flex-row items-end justify-between mb-20 gap-6">
                <motion.h2
                  className="text-3xl md:text-5xl font-light text-white text-left"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                >
                  Todo lo que necesitas. <br/> <span className="text-white/30">Nada que no.</span>
                </motion.h2>
                <p className="text-white/40 text-sm max-w-xs text-right hidden md:block pb-2">
                    Una suite completa de herramientas diseñada para la vida nocturna moderna.
                </p>
            </div>
            
            {/* EL GRID BENTO: 4 Columnas Base */}
            <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[200px] gap-4">
              {bentoFeatures.map((feature, i) => (
                <BentoCard 
                    key={feature.title} 
                    {...feature} 
                    delay={0.1 + i * 0.05} 
                />
              ))}
            </div>
          </div>
        </motion.section>
      )}

      {/* --- CTA FINAL --- */}
      <div className="relative h-[80vh] w-full overflow-hidden flex items-center justify-center">
        <img src="/backgrounds/bg_create.png" alt="Party" className="absolute inset-0 w-full h-full object-cover opacity-50 grayscale hover:grayscale-0 transition-all duration-1000 ease-in-out" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303]" />
        
        <div className="relative z-10 text-center px-6">
            <h2 className="text-5xl md:text-8xl font-bold text-white mb-8 tracking-tighter">
                Ready to Go?
            </h2>
            <button 
                onClick={() => navigate('/register')}
                className="px-10 py-5 bg-white text-black rounded-full font-bold text-lg tracking-wide hover:scale-105 transition-transform duration-300 shadow-[0_0_40px_-10px_rgba(255,255,255,0.5)]"
            >
                REGISTRATE
            </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}